import React, { useState, useEffect, useCallback } from "react";
import { getCompanyDashboardStats } from "../api/dashboard";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  DocumentTextIcon,
  CheckCircleIcon,
  ClockIcon,
  UserGroupIcon,
  ExclamationCircleIcon,
  XCircleIcon,
  UserIcon as IconUserIcon,
  CalendarIcon as IconCalendarIcon,
  BellIcon,
  BuildingOfficeIcon,
  Squares2X2Icon,
} from "@heroicons/react/24/outline";
import CompanyAvatar from "../components/CompanyAvatar";
import BreadCrumb from "../components/common/BreadCrumb";
import { usePageHeader } from "../contexts/PageHeaderContext";
import { Doughnut, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement,
);

const StatCard = ({ title, value, icon: Icon, variant = "primary" }) => {
  const iconColor =
    {
      primary: "text-primary",
      secondary: "text-secondary",
      warning: "text-warning",
      danger: "text-danger",
    }[variant] || "text-primary";
  return (
    <div className="bg-white rounded-2xl p-6 border border-gray-200 relative">
      <p className="text-sm font-medium text-gray-500">{title}</p>
      <p className="font-heading text-[40px] font-bold text-gray-900 mt-1">
        {value}
      </p>
      <div className="mt-4 flex justify-end absolute right-6 top-6">
        <Icon className={`h-[50px] w-[50px] ${iconColor}`} />
      </div>
    </div>
  );
};

const ActivityItem = ({ activity }) => {
  const getActivityIcon = (status) => {
    switch (status) {
      case "COMPLETED":
        return (
          <div className="flex-shrink-0 w-10 h-10 bg-secondary/10 rounded-lg flex items-center justify-center">
            <CheckCircleIcon className="h-5 w-5 text-secondary" />
          </div>
        );
      case "IN_PROGRESS":
        return (
          <div className="flex-shrink-0 w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <ClockIcon className="h-5 w-5 text-primary" />
          </div>
        );
      case "EXPIRED":
        return (
          <div className="flex-shrink-0 w-10 h-10 bg-danger/10 rounded-lg flex items-center justify-center">
            <ExclamationCircleIcon className="h-5 w-5 text-danger" />
          </div>
        );
      case "DECLINED":
        return (
          <div className="flex-shrink-0 w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
            <XCircleIcon className="h-5 w-5 text-warning" />
          </div>
        );
      default:
        return (
          <div className="flex-shrink-0 w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
            <DocumentTextIcon className="h-5 w-5 text-gray-500" />
          </div>
        );
    }
  };

  return (
    <div className="overflow-hidden border-b border-gray-200/60 last:border-b-0">
      <div className="p-4">
        <div className="flex flex-col items-start">
          <div className="flex-1 flex gap-4">
            {getActivityIcon(activity.status)}
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-gray-800 truncate max-w-[200px] text-ellipsis overflow-hidden">
                  {activity.name}
                </h3>
                <div className="mt-1 flex items-center text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <IconUserIcon className="w-4 h-4 text-gray-400" />{" "}
                    {activity?.userId?.name}
                  </span>
                </div>
                <span className="text-xs flex items-center gap-1 text-gray-500 mt-2">
                  <IconCalendarIcon className="w-4 h-4 text-gray-400" />{" "}
                  {new Date(activity.createdAt).toLocaleDateString()}
                </span>
                {activity.recipients?.length > 0 && (
                  <div className="mt-2 flex items-center text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <UserGroupIcon className="w-4 h-4 text-gray-400" />{" "}
                      {activity?.recipients?.length} recipient(s)
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const getPlanDisplayName = (planId) => {
  if (!planId) return "N/A";
  const planNameMap = {
    FREE: "Free",
    STANDARD: "Standard",
    BUSINESS: "Business",
    BUSINESS_PRO: "Business Pro",
    GROUP_STARTER: "Group Starter",
    GROUP_PRO: "Group Pro",
    ENTERPRISE_HOLDING: "Enterprise Holding",
    STANDARD_IN: "Standard",
    BUSINESS_IN: "Business",
    BUSINESS_PRO_IN: "Business Pro",
  };
  return planNameMap[planId] || planId;
};

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scope, setScope] = useState("company"); // 'company' | 'org'
  const { user, organization, activeCompany, companies } = useAuth();
  const navigate = useNavigate();
  const { setHeaderContent } = usePageHeader();

  const isOrgOwner = !!(organization && user?.organizationId);
  const hasMultipleCompanies = companies && companies.length > 1;
  const canViewOrgScope = isOrgOwner && hasMultipleCompanies;

  const getUsagePercentage = (used, allowed) => {
    if (!allowed || allowed <= 0) return 0;
    return Math.round((used / allowed) * 100);
  };

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getCompanyDashboardStats(scope);
      setStats(data);
    } catch (err) {
      setError(err.message || "Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  }, [scope]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Set page header for Navbar
  useEffect(() => {
    if (loading) {
      setHeaderContent(
        <>
          <div>
            <div className="h-4 w-32 bg-gray-200 rounded mb-2 animate-pulse" />
            <div className="h-9 w-48 bg-gray-200 rounded-lg mb-2 animate-pulse" />
            <div className="h-4 w-32 bg-gray-100 rounded animate-pulse" />
          </div>
          <div className="flex gap-3">
            <div className="h-12 w-40 bg-gray-100 rounded-full animate-pulse" />
          </div>
        </>,
      );
    } else if (error) {
      setHeaderContent(null);
    } else {
      setHeaderContent(
        <>
          <div>
            <BreadCrumb
              items={[{ label: "Dashboard" }]}
              className="mb-2"
            />
            <h1 className="font-heading text-xl sm:text-2xl font-bold text-gray-900">
              Welcome back!
            </h1>
            {activeCompany && (
              <p className="text-gray-500 text-xs mt-1 flex items-center gap-1">
                <BuildingOfficeIcon className="w-4 h-4" />
                {scope === "org"
                  ? organization?.name || "All Companies"
                  : activeCompany.name}
              </p>
            )}
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {canViewOrgScope && (
              <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 shadow-sm">
                <button
                  onClick={() => setScope("company")}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all duration-200 min-w-0 ${
                    scope === "company"
                      ? "bg-white text-gray-900 shadow-sm"
                      : "text-gray-500 hover:text-gray-700 hover:bg-white/70"
                  }`}
                  title={`View stats for ${activeCompany?.name || "this company"}`}
                >
                  <div
                    className={`shrink-0 rounded p-0.5 ${
                      scope === "company"
                        ? "ring-1 ring-gray-200 bg-white"
                        : "bg-gray-200/60"
                    }`}
                  >
                    <CompanyAvatar company={activeCompany} size="xs" />
                  </div>
                  <span className="truncate max-w-[100px]">
                    {activeCompany?.name || "This Company"}
                  </span>
                </button>
                <button
                  onClick={() => setScope("org")}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all duration-200 ${
                    scope === "org"
                      ? "bg-white text-gray-900 shadow-sm"
                      : "text-gray-500 hover:text-gray-700 hover:bg-white/70"
                  }`}
                  title={`View consolidated stats for ${organization?.name || "all companies"}`}
                >
                  <div
                    className={`w-5 h-5 rounded flex items-center justify-center shrink-0 transition-colors duration-200 ${
                      scope === "org"
                        ? "bg-primary/15 text-primary"
                        : "bg-gray-200/60 text-gray-500"
                    }`}
                  >
                    <Squares2X2Icon className="w-3.5 h-3.5" />
                  </div>
                  <span>All Companies</span>
                </button>
              </div>
            )}
          </div>
        </>,
      );
    }
    return () => setHeaderContent(null);
  }, [
    loading,
    error,
    scope,
    activeCompany,
    organization,
    stats,
    canViewOrgScope,
    setHeaderContent,
    navigate,
  ]);

  // Re-fetch when user switches company
  useEffect(() => {
    const handler = () => fetchStats();
    window.addEventListener("company-switched", handler);
    return () => window.removeEventListener("company-switched", handler);
  }, [fetchStats]);

  if (loading) {
    return (
      <div className=" px-6 animate-pulse">
        {/* Stats grid skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="bg-white rounded-2xl shadow-sm p-6 animate-pulse"
            >
              <div className="h-4 w-24 bg-gray-200 rounded mb-3" />
              <div className="h-8 w-16 bg-gray-200 rounded" />
              <div className="mt-4 flex justify-end">
                <div className="h-8 w-8 bg-gray-200 rounded" />
              </div>
            </div>
          ))}
        </div>

        {/* Main content skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent Activity skeleton */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-sm">
              <div className="p-6 border-b border-gray-200">
                <div className="h-6 w-36 bg-gray-200 rounded mb-2" />
                <div className="h-5 w-20 bg-gray-100 rounded" />
              </div>
              <div className="p-4 space-y-4">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div
                    key={i}
                    className="flex gap-4 p-4 border-b border-gray-100 last:border-0"
                  >
                    <div className="w-10 h-10 bg-gray-200 rounded-lg shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="h-4 w-40 bg-gray-200 rounded mb-2" />
                      <div className="h-3 w-32 bg-gray-100 rounded mb-1" />
                      <div className="h-3 w-24 bg-gray-100 rounded" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Charts skeleton */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-2xl shadow-sm p-6 animate-pulse">
              <div className="h-6 w-40 bg-gray-200 rounded mb-6" />
              <div className="h-80 bg-gray-100 rounded-xl" />
            </div>
            <div className="bg-white rounded-2xl shadow-sm p-6 animate-pulse">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <div className="h-6 w-36 bg-gray-200 rounded mb-6" />
                  <div className="h-64 bg-gray-100 rounded-xl" />
                </div>
                <div>
                  <div className="h-6 w-28 bg-gray-200 rounded mb-6" />
                  <div className="space-y-4">
                    <div className="h-16 bg-gray-100 rounded-xl" />
                    <div className="h-16 bg-gray-100 rounded-xl" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500">Error loading dashboard: {error}</p>
      </div>
    );
  }

  const formatMonth = (year, month) =>
    new Date(year, month - 1).toLocaleString("default", {
      month: "short",
      year: "numeric",
    });

  const monthlyData = {
    labels:
      stats?.documentsByMonth?.map((m) =>
        formatMonth(m._id.year, m._id.month),
      ) || [],
    datasets: [
      {
        label: "Documents",
        data: stats?.documentsByMonth?.map((m) => m.count) || [],
        backgroundColor: [
          "rgba(26,110,219,0.8)",
          "rgba(16,185,129,0.8)",
          "rgba(245,158,11,0.8)",
          "rgba(239,68,68,0.8)",
          "rgba(26,110,219,0.6)",
          "rgba(16,185,129,0.6)",
          "rgba(245,158,11,0.6)",
          "rgba(239,68,68,0.6)",
          "rgba(26,110,219,0.5)",
          "rgba(16,185,129,0.5)",
          "rgba(245,158,11,0.5)",
          "rgba(239,68,68,0.5)",
        ],
        borderColor: [
          "rgb(26,110,219)",
          "rgb(16,185,129)",
          "rgb(245,158,11)",
          "rgb(239,68,68)",
          "rgb(26,110,219)",
          "rgb(16,185,129)",
          "rgb(245,158,11)",
          "rgb(239,68,68)",
          "rgb(26,110,219)",
          "rgb(16,185,129)",
          "rgb(245,158,11)",
          "rgb(239,68,68)",
        ],
        borderWidth: 1,
        borderRadius: 6,
      },
    ],
  };

  const statusData = {
    labels: stats?.documentsByStatus?.map((s) => s._id) || [],
    datasets: [
      {
        data: stats?.documentsByStatus?.map((s) => s.count) || [],
        backgroundColor:
          stats?.documentsByStatus?.map((s) => {
            switch (s._id) {
              case "COMPLETED":
                return "rgba(16,185,129,0.8)";
              case "IN_PROGRESS":
                return "rgba(26,110,219,0.8)";
              case "EXPIRED":
                return "rgba(239,68,68,0.8)";
              case "DECLINED":
                return "rgba(245,158,11,0.8)";
              default:
                return "rgba(156,163,175,0.8)";
            }
          }) || [],
        borderColor:
          stats?.documentsByStatus?.map((s) => {
            switch (s._id) {
              case "COMPLETED":
                return "rgb(16,185,129)";
              case "IN_PROGRESS":
                return "rgb(26,110,219)";
              case "EXPIRED":
                return "rgb(239,68,68)";
              case "DECLINED":
                return "rgb(245,158,11)";
              default:
                return "rgb(156,163,175)";
            }
          }) || [],
        borderWidth: 1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: "bottom" } },
  };

  return (
    <div className="px-6 py-5">
      {/* Consolidated banner */}
      {stats?.isConsolidated && (
        <div className="mb-6 bg-primary/5 rounded-2xl px-4 py-3 flex items-center gap-2 text-sm text-primary shadow-sm">
          <BuildingOfficeIcon className="w-4 h-4 shrink-0" />
          Showing consolidated data across all{" "}
          {stats.companyBreakdown?.length || companies.length} companies in your
          organization.
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Total Documents"
          value={stats?.totalDocuments || 0}
          icon={DocumentTextIcon}
          variant="primary"
        />
        <StatCard
          title="Signed Documents"
          value={stats?.signedDocuments || 0}
          icon={CheckCircleIcon}
          variant="secondary"
        />
        <StatCard
          title="Pending Documents"
          value={stats?.pendingDocuments || 0}
          icon={ClockIcon}
          variant="warning"
        />
        <StatCard
          title="Expired Documents"
          value={stats?.expiredDocuments || 0}
          icon={ExclamationCircleIcon}
          variant="danger"
        />
      </div>

      {/* Company Breakdown (org scope only) */}
      {stats?.isConsolidated && stats?.companyBreakdown?.length > 0 && (
        <div className="mb-8 bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="font-heading text-xl font-semibold text-gray-900">
              Breakdown by Company
            </h2>
          </div>
          <div className="p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {stats.companyBreakdown.map((company, idx) => (
              <div
                key={company.companyId || idx}
                className="rounded-2xl p-4 bg-gray-50/50 shadow-sm"
              >
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">
                    {company.companyName?.[0]?.toUpperCase() || "?"}
                  </div>
                  <span className="text-sm font-semibold text-gray-800 truncate">
                    {company.companyName}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="text-gray-500">
                    Total:{" "}
                    <span className="font-semibold text-gray-800">
                      {company.total}
                    </span>
                  </div>
                  <div className="text-gray-500">
                    Signed:{" "}
                    <span className="font-semibold text-secondary">
                      {company.signed}
                    </span>
                  </div>
                  <div className="text-gray-500">
                    Pending:{" "}
                    <span className="font-semibold text-warning">
                      {company.pending}
                    </span>
                  </div>
                  <div className="text-gray-500">
                    Expired:{" "}
                    <span className="font-semibold text-danger">
                      {company.expired}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Activity */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-xl font-semibold text-gray-900">
                  Recent Activity
                </h2>
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                  {stats?.recentActivity?.length || 0} activities
                </span>
              </div>
            </div>
            <div className="max-h-[600px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
              {stats?.recentActivity?.length > 0 ? (
                <div className="space-y-1">
                  {stats.recentActivity.map((activity, index) => (
                    <ActivityItem key={index} activity={activity} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <DocumentTextIcon className="h-8 w-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No recent activity
                  </h3>
                  <p className="text-sm text-gray-500 max-w-xs mx-auto">
                    Get started by creating a new document or sending a
                    signature request.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Charts Section */}
        <div className="lg:col-span-2">
          <div className="grid grid-cols-1 gap-6">
            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
              <div className="px-6 pt-6 pb-2">
                <h2 className="font-heading text-xl font-semibold text-gray-900">
                  Document Trends
                </h2>
              </div>
              <div className="px-6 pb-6 h-80">
                <Bar data={monthlyData} options={chartOptions} />
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6">
                <div>
                  <h2 className="font-heading text-xl font-semibold text-gray-900 mb-4">
                    Document Status
                  </h2>
                  <div className="h-64">
                    <Doughnut data={statusData} options={chartOptions} />
                  </div>
                </div>
                <div>
                  <h2 className="font-heading text-xl font-semibold text-gray-900 mb-4">
                    Performance
                  </h2>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center p-4 rounded-2xl bg-gray-50/50 shadow-sm">
                      <div>
                        <span className="text-sm font-medium text-gray-600">
                          Completion Rate
                        </span>
                        <p className="text-xs text-gray-500 mt-1">
                          Successfully processed
                        </p>
                      </div>
                      <span className="font-heading text-2xl font-bold text-secondary">
                        {stats?.companyStats?.completionRate || "0"}%
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-4 rounded-2xl bg-gray-50/50 shadow-sm">
                      <div>
                        <span className="text-sm font-medium text-gray-600">
                          Total Requests
                        </span>
                        <p className="text-xs text-gray-500 mt-1">
                          Document requests
                        </p>
                      </div>
                      <span className="font-heading text-2xl font-bold text-primary">
                        {stats?.companyStats?.totalRequests || 0}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
